import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cdFetch, cdPost } from "../api";
import { queryKeys } from "../queryKeys";
import {
  calculateNetworkingDaysUntilEndOfMonth,
  getNetworkingDaysInMonth,
  getNetworkingDaysUpToToday,
  revertTransform,
  transformYearDate,
} from "../../utils/helpers/mapModulesToIcon";
import moment from "moment";
import {
  formatNumberwithCommas,
  formatWithCommas,
  getRemainingVolume,
  numberFormatterInK,
} from "../../utils/formatter";

export const useCostComplianceData = (
  type: string,
  commodity: string,
  vendor: any[] | null | undefined,
  district: any[] | null | undefined,
  month: any[] | null | undefined
) => {
  const shouldFetch = !!type && !!commodity;
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.costCompliance,
      type,
      commodity,
      vendor,
      district,
      month,
    ],
    queryFn: () =>
      getCostComplianceData(type, commodity, vendor, district, month),
    staleTime: 20000,
    enabled: shouldFetch && district && district.length ? true : false,
  });
  // const vendorData: RenegotiateVendorDetailsData = { ...data?.data };
  const complianceData = { ...data?.data };
  return { complianceData, isLoading };
};

export const useDownloadCostComplianceData = (
  commodity: string,
  month: any[] | null | undefined,
  vendor: any[] | null | undefined,
  district: any[] | null | undefined,
  isEnabled: boolean
) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.costCompliance, commodity, vendor, district, month],
    queryFn: () =>
      getCostComplianceExcelData(commodity, vendor, district, month),
    staleTime: 20000,
    enabled: isEnabled,
  });
  const excelData = data ? [...data?.data?.data] : null;
  return { excelData, isLoading };
};

export const useVolumeComplianceDistrictsData = (
  navItem: string,
  commodity: string,
  vendor: string,
  district: any,
  month: string[]
) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.volumeComplianceDistricts,
      commodity,
      vendor,
      district,
      month,
    ],
    queryFn: () =>
      getVolumeComplianceDistrictData(commodity, district, month, vendor),
    staleTime: 20000,
    enabled: navItem === "details" && !!vendor,
  });
  // const vendorData: RenegotiateVendorDetailsData = { ...data?.data };
  const volumeDataForDistricts = { ...data?.data?.data };
  return { volumeDataForDistricts, isLoadingVolume: isLoading };
};

export const useVolumeComplianceData = (
  navItem: string,
  commodity: string,
  district: [] | string,
  month: any[] | null | undefined
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.volumeCompliance, commodity, district, month],
    queryFn: () => getVolumeComplianceData(commodity, district, month),
    staleTime: 20000,
    enabled:
      !!commodity &&
      navItem !== "details" &&
      ((Array.isArray(district) && district.length > 0) ||
        (typeof district === "string" && district.length > 0)),
  });
  let volumeComplianceData = null;
  if (data?.data?.data) {
    let total: any = {};
    let result = [...data?.data?.data?.vol_data];
    let totalCommitedVolume = 0;
    let totalActualVolume = 0;
    let totalOverspendVolume = 0;
    let totalOverSpend = 0;
    data?.data?.data?.vol_data?.map((item: any) => {
      totalCommitedVolume = totalCommitedVolume + item?.Total_Commited_Volume;
      totalOverspendVolume = totalOverspendVolume + item?.Overspend;
      totalActualVolume = totalActualVolume + item?.Total_Actual_Volume;
      total["Total_Commited_Volume"] = totalCommitedVolume;
      total["vendor"] = "TOTAL";
      total["Overspend"] = totalOverspendVolume;
      total["Total_Actual_Volume"] = totalActualVolume;
      totalOverSpend = totalOverSpend + item?.Total_Overspend;
    });
    result?.push(total);
    const momentObj = moment.utc(month, "MMM-YY");
    const monthNumber = momentObj.month() + 1; // Add 1 for 1-based index
    let dateTobeCalculatedFor = moment.utc(
      data?.data?.data?.refreshed_date,
      "D"
    );
    let formattedDay = dateTobeCalculatedFor.format("D");
    let newResult = result?.map((item: any) => {
      let remaining_volume_tls = getRemainingVolume(item);
      item["remaining_vol_tls"] = remaining_volume_tls;
      item["comm_pur_pac"] =
        (
          (item?.Total_Commited_Volume /
            getNetworkingDaysInMonth(moment.utc().year(), monthNumber)) *
            10
        ) / 10;
      if (monthNumber !== moment.utc().month() + 1) {
        item["acc_pur_pac"] =
          (
            (item?.Total_Actual_Volume /
              getNetworkingDaysInMonth(moment.utc().year(), monthNumber)) *
              10
          ) / 10;
        item["req_pur_pac"] = "-";
      } else {
        const workingDay = getNetworkingDaysUpToToday(
          parseInt(formattedDay, 10)
        );
        item["acc_pur_pac"] =
          workingDay !== 0
            ? ((item?.Total_Actual_Volume / workingDay) * 10) / 10
            : 0.0;
        item["req_pur_pac"] =
          Math.round(
            (remaining_volume_tls /
              calculateNetworkingDaysUntilEndOfMonth(
                data?.data?.data?.refreshed_date
              )) *
              10
          ) / 10;
      }

      return item;
    });
    const volumeComplianceData = {
      result: newResult,
      overspend: totalOverSpend,
      refreshed_date: data?.data?.data?.refreshed_date,
    };
    return { volumeComplianceData, isLoading };
  }

  return { volumeComplianceData, isLoading };
};

export const useUpdateVolumeComplianceActualVolume = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.volumeCompliance, "actual volume update"],
    mutationFn: (payload: { updates: any[] }) =>
      updateVolumeComplianceActualVolume(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.volumeCompliance],
      });
      console.log("ACTUAL VOLUME UPDATE SUCCESS", res);
    },
    onError: (error) => {
      console.log("ACTUAL VOLUME UPDATE ERROR", error);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isPending: mutation.isPending,
  };
};

const updateVolumeComplianceActualVolume = (payload: { updates: any[] }) => {
  const res = cdPost(`/updateVolumeComplianceActualVolume`, payload);
  return res;
};

export const useComplianceFilters = (
  userEmail: string | null,
  source: string,
  commodity: string
) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.complianceFilters, userEmail, source, commodity],
    queryFn: () => getComplianceFilters(userEmail, source, commodity),
    staleTime: 20000,
    enabled:
      userEmail &&
      userEmail !== null &&
      userEmail !== "" &&
      userEmail !== undefined
        ? true
        : false,
  });
  const complianceFilters = { ...data?.data?.data };

  // Transform the year_date field
  if (complianceFilters?.dates && source !== "cost") {
    complianceFilters.dates = transformYearDate(complianceFilters?.dates);
  }
  let filtersLoaded = isLoading;
  return { complianceFilters, filtersLoaded };
};

const getCostComplianceData = (
  type: string,
  commodity: string,
  vendor: string[] | null | undefined,
  district: string[] | null | undefined,
  month: string[] | null | undefined
) => {
  const revertedMonth = month;

  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${commodity}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (vendor && vendor.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }
  if (revertedMonth && revertedMonth.length > 0) {
    qParams += `start_of_week=${JSON.stringify(revertedMonth)}&`;
  }
  if (type) {
    qParams += `source=${type}&`;
  }
  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getCostComplianceTableData${qParams}`);
  return res;
};

const getComplianceFilters = (
  userEmail: string | null,
  source: string,
  commodity: string
) => {
  let qParams = "";
  if (
    userEmail &&
    userEmail !== null &&
    userEmail !== "" &&
    userEmail !== undefined
  ) {
    qParams += `email=${userEmail}&`;
  }
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${commodity}&`;
  }
  if (source && source.length > 0) {
    qParams += `source=${source}`;
  }
  const res = cdFetch(`/getAllFiltersCostCompliance?${qParams}`);
  return res;
};

const getVolumeComplianceData = (
  commodity: string,
  district: [] | string,
  month: string[] | null | undefined
) => {
  const revertedMonth = month ? revertTransform(month) : null;

  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify([commodity])}&`;
  }
  if (district && district.length > 0) {
    if (Array.isArray(district))
      qParams += `district=${JSON.stringify(district)}&`;
    else qParams += `district=${JSON.stringify([district])}&`;
  }
  if (revertedMonth && revertedMonth.length > 0) {
    if (Array.isArray(revertedMonth))
      qParams += `month=${JSON.stringify(revertedMonth)}&`;
    else qParams += `month=${JSON.stringify([revertedMonth])}&`;
  }
  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getVolumeComplianceDetailsData${qParams}`);
  return res;
};

const getVolumeComplianceDistrictData = (
  commodity: string,
  district: string[] | null | undefined,
  month: string[],
  vendor: string
) => {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (month && month.length > 0) {
    if (Array.isArray(month))
      qParams += `month=${JSON.stringify(revertTransform(month))}&`;
    else qParams += `month=${JSON.stringify([revertTransform(month)])}&`;
  }

  if (month && month.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }

  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getVolumeComplianceDetailsForDistrict${qParams}`);
  return res;
};

const getCostComplianceExcelData = (
  commodity: string,
  vendor: string[] | null | undefined,
  district: string[] | null | undefined,
  month: string[] | null | undefined
) => {
  const revertedMonth = month;

  let qParams = "";
  if (commodity && commodity.length > 0) {
    if (Array.isArray(commodity)) {
      qParams += `commodity=${JSON.stringify(commodity)}&`;
    } else {
      qParams += `commodity=${JSON.stringify([commodity])}&`;
    }
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (vendor && vendor.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }
  if (revertedMonth && revertedMonth.length > 0) {
    qParams += `start_of_week=${JSON.stringify(revertedMonth)}&`;
  }
  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getCostComplianceDownloadData${qParams}`);
  return res;
};

export const useAllocationComplianceFilters = (
  userEmail: string | null,
  commodity: string | null
) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.allocationComplianceFilters, userEmail, commodity],
    queryFn: () => getAllocationComplianceFilters(userEmail, commodity),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    enabled:
      userEmail &&
      userEmail !== "" &&
      userEmail !== null &&
      userEmail !== undefined
        ? true
        : false,
  });

  const allocationComplianceFilters = { ...data?.data?.data };

  let filtersLoaded = isLoading;
  return { allocationComplianceFilters, filtersLoaded };
};

const getAllocationComplianceFilters = (
  userEmail: string | null,
  commodity: string | null
) => {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  qParams += `email=${JSON.stringify(userEmail)}&`;
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getVendorShareFilters${qParams}`);
  return res;
};

export const useAllocationComplianceData = (
  commodity: string,
  district: string[],
  baseline: string,
  trendView: string,
  baseSKU: string,
  isPartnerView: boolean,
  userEmail: string | null
) => {
  const isEnabledFn = () => {
    if (isPartnerView) {
      if (commodity === "PVC Conduit") return false;
      else {
        if (
          commodity &&
          commodity.length > 0 &&
          district &&
          district.length > 0 &&
          baseline &&
          baseline.length > 0 &&
          trendView &&
          trendView.length > 0
        ) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      if (
        commodity &&
        commodity.length > 0 &&
        baseline &&
        baseline.length > 0 &&
        trendView &&
        trendView.length > 0
      ) {
        return true;
      } else {
        return false;
      }
    }
  };
  const { data, isLoading } = useQuery({
    queryKey: [
      queryKeys.allocationComplianceData,
      commodity,
      district,
      baseline,
      trendView,
      baseSKU,
      isPartnerView,
      userEmail,
    ],
    queryFn: () =>
      getAllocationComplianceData(
        commodity,
        district,
        baseline,
        trendView,
        baseSKU,
        isPartnerView,
        userEmail
      ),
    staleTime: 20000,
    enabled:
      isEnabledFn() &&
      userEmail &&
      userEmail !== null &&
      userEmail !== undefined &&
      userEmail !== ""
        ? true
        : false,
  });
  let allocationData = { ...data?.data?.data };

  let isEstimated = false;

  if (allocationData.MTDSummary) {
    allocationData.MTDSummary.data = allocationData?.MTDSummary?.data?.map(
      (item: any) => {
        if (item.children?.length > 0) {
          item.children = item?.children?.filter(
            (innerItem: any) => innerItem.name !== null
          );
        }
        return item;
      }
    );
  }

  if (allocationData?.PoCost) {
    allocationData?.PoCost?.vendors.forEach((vendor: any) => {
      allocationData?.PoCost.data.forEach((ele: any) => {
        if (
          ele[`${vendor}_actualPoCost`] === null &&
          ele[`${vendor}_estimatedPoCost`] !== null
        ) {
          isEstimated = true;
        }
      });
    });
  }

  const mtdIsEstimated: Record<string, number> =
    allocationData?.MTDSummary?.isEstimated ?? {};

  let allocationDataLoaded = isLoading;
  return {
    allocationData,
    allocationDataLoaded,
    isEstimated,
    mtdIsEstimated,
  };
};

const getAllocationComplianceData = (
  commodity: string,
  district: string[],
  baseline: string,
  trendView: string,
  baseSKU: string,
  isPartnerView: boolean,
  userEmail: string | null
) => {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (baseline && baseline.length > 0) {
    qParams += `baseline=${JSON.stringify(baseline)}&`;
  }
  if (trendView && trendView.length > 0) {
    qParams += `trendView=${JSON.stringify(trendView)}&`;
  }
  if (baseSKU && baseSKU.length > 0) {
    qParams += `base_sku=${encodeURIComponent(baseSKU)}&`;
  }

  qParams += `isPartnerView=${JSON.stringify(isPartnerView)}&`;

  qParams += `email=${JSON.stringify(userEmail)}&`;

  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  const res = cdFetch(`/getVendorMarketShareData${qParams}`);
  return res;
};

export const handleNullAndZero = (arr: any) => {
  let result: any = [];
  arr.map((obj: any) => {
    Object.keys(obj).map((key: string) => {
      if (obj[key] === 0 || obj[key] === "0%" || obj[key] === "0.00%") {
        obj[key] = "-";
      }
    });
    result.push(obj);
  });
  return result;
};

export const handleformatforchild = (arr: any, vendors: any) => {
  const newArray = [
    ...arr.map((mtdObj: any) => {
      const ele = vendors
        .map((vendor: string) => ({
          [vendor]: !isNaN(mtdObj[vendor])
            ? formatNumberwithCommas(mtdObj[vendor])
            : mtdObj[vendor],
        }))
        .reduce((a: any, c: any) => ({ ...a, ...c }), {
          GainsMonthlyForecast:
            mtdObj["GainsMonthlyForecast"] !== null
              ? !isNaN(mtdObj["GainsMonthlyForecast"])
                ? formatNumberwithCommas(mtdObj["GainsMonthlyForecast"])
                : mtdObj["GainsMonthlyForecast"]
              : null,
          Total: !isNaN(mtdObj["Total"])
            ? formatNumberwithCommas(mtdObj["Total"])
            : mtdObj["Total"],
          key: mtdObj["key"],
          name: mtdObj["name"],
        });
      return ele;
    }),
  ];
  return newArray;
};

export const useVolumeComplianceDistrictsDataDownload = (
  commodity: string,
  district: string | null | undefined,
  month: string[],
  vendor: string[]
) => {
  let qParams = "";
  if (commodity && commodity.length > 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (district && district.length > 0) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (month && month.length > 0) {
    if (Array.isArray(month))
      qParams += `month=${JSON.stringify(revertTransform(month))}&`;
    else qParams += `month=${JSON.stringify([revertTransform(month)])}&`;
  }

  if (month && month.length > 0) {
    qParams += `vendor=${JSON.stringify(vendor)}&`;
  }

  // Remove the trailing '&' and prepend '?' if there are any query parameters
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getVolumeComplianceDetailsForDistrict${qParams}`);
  return res;
};
